import { useApiQuery } from "hooks/useApiQuery/useApiQuery";
import { CACHE_KEYS } from "commons/constants";
import { renderErrorNotifications } from "helpers/error.helpers";

/**
 * Loads live city and area master lists once (cached 30 min).
 *
 * cities / areas           → raw API records
 * cityOptions              → [{ label, value: city_code }]
 * getAreaOptions(cityCode) → [{ label, value: area_code }]
 * getCityName / getAreaDisplayName → lookup helpers for display copy
 */
export default function useGeoMasterData() {
  const { data: citiesData, isFetching: isFetchingCities } = useApiQuery({
    queryKey: [CACHE_KEYS.CITY_MASTER_LIST, "live"],
    url: "v1/community-portal/master-data/city-master/list",
    params: { page_size: 10000, current_page: 1, is_live: true },
    staleTimeInMinutes: 30,
    onError: (errors) => renderErrorNotifications(errors),
  });

  const { data: areasData, isFetching: isFetchingAreas } = useApiQuery({
    queryKey: [CACHE_KEYS.AREA_MASTER_LIST, "live"],
    url: "v1/community-portal/master-data/area-master/list",
    params: { page_size: 10000, current_page: 1, is_live: true },
    staleTimeInMinutes: 30,
    onError: (errors) => renderErrorNotifications(errors),
  });

  const cities = citiesData?.data ?? [];
  const areas = areasData?.data ?? [];

  const cityOptions = cities.map((city) => ({
    label: `${city.name} (${city.city_code})`,
    value: city.city_code,
  }));

  const areasByCityCode = areas.reduce((acc, area) => {
    if (!acc[area.city_code]) {
      acc[area.city_code] = [];
    }
    acc[area.city_code].push(area);
    return acc;
  }, {});

  const getAreaOptions = (cityCode) =>
    (areasByCityCode[cityCode] ?? []).map((area) => ({
      label: `${area.display_name} (${area.area_code})`,
      value: area.area_code,
    }));

  const getCityName = (cityCode) =>
    cities.find((city) => city.city_code === cityCode)?.name;

  const getAreaDisplayName = (areaCode) =>
    areas.find((area) => area.area_code === areaCode)?.display_name;

  return {
    cities,
    areas,
    cityOptions,
    areasByCityCode,
    getAreaOptions,
    getCityName,
    getAreaDisplayName,
    isFetching: isFetchingCities || isFetchingAreas,
  };
}
